/**
 * @file categoryStyles.ts
 * @brief Dynamic category badge styling with fallbacks for enum_master values.
 * @author Nestbotics
 * @date 2026-06-19
 */

import type { EnumMasterItem } from '@/services/api/enumMasterService';

export interface CategoryStyle {
  bgcolor: string;
  color: string;
  label: string;
}

const KNOWN_CATEGORY_STYLES: Record<string, CategoryStyle> = {
  task: { bgcolor: '#dbeafe', color: '#1e40af', label: 'Task' },
  subtask: { bgcolor: '#e0f2fe', color: '#0369a1', label: 'Subtask' },
  development: { bgcolor: '#dbeafe', color: '#2563eb', label: 'Development' },
  design: { bgcolor: '#f3e8ff', color: '#9333ea', label: 'Design' },
  testing: { bgcolor: '#fef3c7', color: '#d97706', label: 'Testing' },
  documentation: { bgcolor: '#e0e7ff', color: '#4f46e5', label: 'Documentation' },
  'bug-fix': { bgcolor: '#fee2e2', color: '#dc2626', label: 'Bug Fix' },
  feature: { bgcolor: '#d1fae5', color: '#059669', label: 'Feature' },
  research: { bgcolor: '#fce7f3', color: '#db2777', label: 'Research' },
  meeting: { bgcolor: '#cffafe', color: '#0891b2', label: 'Meeting' },
  review: { bgcolor: '#fef9c3', color: '#ca8a04', label: 'Review' },
  support: { bgcolor: '#fee2e2', color: '#dc2626', label: 'Support' },
  maintenance: { bgcolor: '#f5f5f4', color: '#57534e', label: 'Maintenance' },
  deployment: { bgcolor: '#d1fae5', color: '#059669', label: 'Deployment' },
  planning: { bgcolor: '#e0f2fe', color: '#0284c7', label: 'Planning' },
  other: { bgcolor: '#f3f4f6', color: '#6b7280', label: 'Other' },
};

const FALLBACK_PALETTE: CategoryStyle[] = [
  { bgcolor: '#e0e7ff', color: '#4338ca', label: '' },
  { bgcolor: '#fce7f3', color: '#be185d', label: '' },
  { bgcolor: '#ecfccb', color: '#4d7c0f', label: '' },
  { bgcolor: '#ffedd5', color: '#c2410c', label: '' },
  { bgcolor: '#fae8ff', color: '#a21caf', label: '' },
];

/**
 * @brief Builds a lookup map from enum_master rows for display labels.
 * @param categories Enum rows from enum_master.
 * @return Map of keyValue to displayLabel.
 */
export function buildCategoryLabelMap(categories: EnumMasterItem[]): Record<string, string> {
  return categories.reduce<Record<string, string>>((acc, item) => {
    acc[item.keyValue] = item.displayLabel;
    return acc;
  }, {});
}

/**
 * @brief Formats a category slug into a readable label.
 * @param category Category key value.
 * @return Human-readable label.
 */
export function formatCategoryLabel(category: string): string {
  return category
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * @brief Resolves badge styling for any category value.
 * @param category Category key value stored on a task.
 * @param labelMap Optional enum_master label overrides.
 * @return Styling object for chips and badges.
 */
export function getCategoryStyle(
  category?: string | null,
  labelMap?: Record<string, string>
): CategoryStyle | null {
  if (!category) return null;

  const known = KNOWN_CATEGORY_STYLES[category];
  const label = labelMap?.[category] ?? known?.label ?? formatCategoryLabel(category);

  if (known) {
    return { ...known, label };
  }

  const paletteIndex =
    Math.abs(category.split('').reduce((sum, ch) => sum + ch.charCodeAt(0), 0)) %
    FALLBACK_PALETTE.length;
  const palette = FALLBACK_PALETTE[paletteIndex];
  return { bgcolor: palette.bgcolor, color: palette.color, label };
}

/**
 * @brief Resolves a simple color for category badges.
 * @param category Category key value stored on a task.
 * @return Hex color string.
 */
export function getCategoryColor(category?: string | null): string {
  return getCategoryStyle(category)?.color ?? '#6b7280';
}
